/**
 * SharedArrayBuffer-backed ring buffer for float32 audio samples.
 *
 * Layout:
 * - Byte 0-3: read index (Int32, monotonically increasing)
 * - Byte 4-7: write index (Int32, monotonically increasing)
 * - Byte 8+:  sample storage (Float32Array)
 *
 * One "available read" = writeIdx - readIdx samples.
 * Hop size for DFN3 is 480 samples; worker should only pop when >= 480.
 */

export interface SharedRingBufferViews {
  sab: SharedArrayBuffer;
  readIdx: Int32Array;
  writeIdx: Int32Array;
  data: Float32Array;
  capacity: number;
}

const HEADER_BYTES = 8; // two Int32 (read, write)

export function createSharedRingBuffer(capacity: number): SharedRingBufferViews {
  const sab = new SharedArrayBuffer(HEADER_BYTES + capacity * Float32Array.BYTES_PER_ELEMENT);
  const readIdx = new Int32Array(sab, 0, 1);
  const writeIdx = new Int32Array(sab, 4, 1);
  const data = new Float32Array(sab, HEADER_BYTES);
  readIdx[0] = 0;
  writeIdx[0] = 0;
  return { sab, readIdx, writeIdx, data, capacity };
}

export class RingBufferView {
  private readonly sab: SharedArrayBuffer;
  private readonly readIdx: Int32Array; // consumer pointer
  private readonly writeIdx: Int32Array; // producer pointer
  private readonly data: Float32Array;
  private readonly capacity: number;

  constructor(views: SharedRingBufferViews) {
    this.sab = views.sab;
    this.readIdx = views.readIdx;
    this.writeIdx = views.writeIdx;
    this.data = views.data;
    this.capacity = views.capacity;
  }

  availableRead(): number {
    const r = Atomics.load(this.readIdx, 0);
    const w = Atomics.load(this.writeIdx, 0);
    return w - r;
  }

  availableWrite(): number {
    return this.capacity - this.availableRead();
  }

  /**
   * Push samples into the ring. Returns number of samples written.
   */
  push(input: Float32Array): number {
    const writable = Math.min(input.length, this.availableWrite());
    if (writable <= 0) return 0;

    const w = Atomics.load(this.writeIdx, 0);
    const first = Math.min(writable, this.capacity - (w % this.capacity));
    this.data.set(input.subarray(0, first), w % this.capacity);

    const remaining = writable - first;
    if (remaining > 0) {
      this.data.set(input.subarray(first, first + remaining), 0);
    }

    Atomics.store(this.writeIdx, 0, w + writable);
    return writable;
  }

  /**
   * Pop up to `count` samples. Returns a Float32Array view (copy).
   */
  pop(count: number): Float32Array {
    const readable = Math.min(count, this.availableRead());
    if (readable <= 0) return new Float32Array(0);

    const r = Atomics.load(this.readIdx, 0);
    const first = Math.min(readable, this.capacity - (r % this.capacity));
    const out = new Float32Array(readable);
    out.set(this.data.subarray(r % this.capacity, (r % this.capacity) + first), 0);

    const remaining = readable - first;
    if (remaining > 0) {
      out.set(this.data.subarray(0, remaining), first);
    }

    Atomics.store(this.readIdx, 0, r + readable);
    return out;
  }
}
